import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTranslate, useNotify } from 'react-admin';
import { useAppLocale } from '../../hooks/useAppLocale';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import ForumIcon from '@mui/icons-material/Forum';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddReactionIcon from '@mui/icons-material/AddReaction';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HistoryIcon from '@mui/icons-material/History';
import ReplayIcon from '@mui/icons-material/Replay';
import ReplyIcon from '@mui/icons-material/Reply';
import ShortcutIcon from '@mui/icons-material/Shortcut';
import TimerIcon from '@mui/icons-material/Timer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditNoteIcon from '@mui/icons-material/EditNote';
import type { Message, MessageStatus, ChannelType } from '../../types/chat.types';
import { MessageStatusHistoryDialog } from './MessageStatusHistoryDialog';
import { MessageEditHistoryDialog } from './MessageEditHistoryDialog';
import { MessageEditDialog } from './MessageEditDialog';
import { DeleteMessageConfirmDialog } from './DeleteMessageConfirmDialog';
import { EmojiReactionPicker } from './EmojiReactionPicker';
import { MessageAttachment } from './MessageAttachment';
import {
  useMessageActions,
  getChannelCapabilities,
  canEditMessage,
  canDeleteMessage,
  getRemainingTime,
  type ChannelCapabilities,
} from './hooks/useMessageActions';

// WhatsApp-style colors
const WHATSAPP_COLORS = {
  // Light mode
  light: {
    outgoing: '#d9fdd3', // Light green for own messages
    outgoingHover: '#c8edc1',
    incoming: '#ffffff', // White for others' messages
    incomingHover: '#f5f5f5',
    statusIcon: '#667781',
    statusIconRead: '#53bdeb', // Blue tick color
    timestamp: '#667781',
    quoteSelf: '#06cf9c', // Green accent for own/agent quotes
    quoteOther: '#7c3aed', // Purple accent for contact quotes
    senderName: '#06cf9c', // Green for sender name labels
  },
  // Dark mode
  dark: {
    outgoing: '#005c4b', // Dark teal green for own messages
    outgoingHover: '#004d40',
    incoming: '#202c33', // Dark gray for others' messages
    incomingHover: '#2a3942',
    statusIcon: '#8696a0',
    statusIconRead: '#53bdeb',
    timestamp: '#8696a0',
    quoteSelf: '#06cf9c',
    quoteOther: '#7c3aed',
    senderName: '#06cf9c',
  },
};

interface ReactionDisplay {
  emoji: string;
  count: number;
  hasOwnReaction?: boolean;
}

interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  /** Real-time reactions from WebSocket (merged with message.reactions) */
  realtimeReactions?: Array<{
    reaction: string;
    userId?: number;
    contactIdentityId?: number;
  }>;
  /** Current user ID for determining own reactions */
  currentUserId?: number;
  /** Channel type for determining available actions */
  channelType?: ChannelType | null;
  /** Callback when message is updated (edit/delete) - triggers refresh */
  onMessageUpdated?: () => void;
  /** Callback to retry sending a failed message */
  onRetryMessage?: (messageId: number) => void;
  /** Callback when user wants to reply/quote this message */
  onReply?: (message: Message) => void;
  /** Callback when user wants to forward this message */
  onForward?: (message: Message) => void;
}

const MessageStatusIcon = ({ status, isDarkMode }: { status: MessageStatus; isDarkMode?: boolean }) => {
  const colors = isDarkMode ? WHATSAPP_COLORS.dark : WHATSAPP_COLORS.light;

  switch (status) {
    case 'PROCESSING':
      return <AccessTimeIcon fontSize="inherit" sx={{ color: colors.statusIcon, opacity: 0.5 }} />;
    case 'PENDING':
      return <AccessTimeIcon fontSize="inherit" sx={{ color: colors.statusIcon }} />;
    case 'SENT':
      return <DoneIcon fontSize="inherit" sx={{ color: colors.statusIcon }} />;
    case 'RECEIVED':
      return <DoneAllIcon fontSize="inherit" sx={{ color: colors.statusIcon }} />;
    case 'READ':
    case 'PLAYED':
      return <DoneAllIcon fontSize="inherit" sx={{ color: colors.statusIconRead }} />;
    case 'FAILED':
      return <ErrorIcon fontSize="inherit" sx={{ color: '#f44336' }} />;
    default:
      return null;
  }
};

export function MessageBubble({
  message,
  isOwnMessage,
  realtimeReactions,
  currentUserId,
  channelType,
  onMessageUpdated,
  onRetryMessage,
  onReply,
  onForward,
}: MessageBubbleProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const appLocale = useAppLocale();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? WHATSAPP_COLORS.dark : WHATSAPP_COLORS.light;
  const isDeleted = message.isDeleted;
  const isSystem = message.type === 'system';
  const isForwarded = !!message.metadata?.forwardedFrom;
  const isEphemeral = !!message.metadata?.zapy?.ephemeral;

  // Message actions hook
  const { editMessage, deleteMessage, reactToMessage, removeReaction, isLoading, loadingAction } =
    useMessageActions();

  // Channel capabilities
  const capabilities = useMemo(() => getChannelCapabilities(channelType), [channelType]);

  // State for dialogs
  const [statusHistoryOpen, setStatusHistoryOpen] = useState(false);
  const [editHistoryOpen, setEditHistoryOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // State for reaction picker
  const [reactionAnchorEl, setReactionAnchorEl] = useState<HTMLElement | null>(null);
  const reactionPickerOpen = Boolean(reactionAnchorEl);

  // State for message actions menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const menuOpen = Boolean(menuAnchorEl);

  // Check if this is an optimistic message (not yet saved to server)
  const isOptimisticMessage = message.uuid?.startsWith('optimistic-');

  // Calculate action availability
  const canEdit = canEditMessage(message, isOwnMessage, capabilities);
  const canDelete = canDeleteMessage(message, isOwnMessage, capabilities);
  const canReact = capabilities?.canReact && !isOptimisticMessage;
  const canForward = capabilities?.canForward && !isOptimisticMessage && !isDeleted;

  // Get remaining time for edit/delete
  const editRemainingTime = getRemainingTime(message, capabilities?.editTimeLimit ?? null);
  const deleteRemainingTime = getRemainingTime(message, capabilities?.deleteTimeLimit ?? null);

  // Get current user's reaction
  const currentUserReaction = useMemo(() => {
    if (!currentUserId) return null;

    // Check message.reactions first
    const fromMessage = message.reactions?.find((r) => r.userId === currentUserId);
    if (fromMessage) return fromMessage.reaction;

    // Check realtime reactions
    const fromRealtime = realtimeReactions?.find((r) => r.userId === currentUserId);
    return fromRealtime?.reaction ?? null;
  }, [message.reactions, realtimeReactions, currentUserId]);

  // Merge and aggregate reactions from message.reactions and realtimeReactions
  const aggregatedReactions = useMemo((): ReactionDisplay[] => {
    const reactionsByEmoji = new Map<string, Set<string>>();
    const hasOwnReactionByEmoji = new Map<string, boolean>();

    const getReactorKey = (userId?: number | null, contactIdentityId?: number | null) => {
      if (userId) return `user:${userId}`;
      if (contactIdentityId) return `contact:${contactIdentityId}`;
      return `unknown:${Math.random()}`;
    };

    if (message.reactions) {
      for (const r of message.reactions) {
        const reactorKey = getReactorKey(r.userId, r.contactIdentityId);
        const reactors = reactionsByEmoji.get(r.reaction) || new Set();
        reactors.add(reactorKey);
        reactionsByEmoji.set(r.reaction, reactors);

        if (currentUserId && r.userId === currentUserId) {
          hasOwnReactionByEmoji.set(r.reaction, true);
        }
      }
    }

    if (realtimeReactions) {
      for (const r of realtimeReactions) {
        const reactorKey = getReactorKey(r.userId, r.contactIdentityId);
        const reactors = reactionsByEmoji.get(r.reaction) || new Set();
        reactors.add(reactorKey);
        reactionsByEmoji.set(r.reaction, reactors);

        if (currentUserId && r.userId === currentUserId) {
          hasOwnReactionByEmoji.set(r.reaction, true);
        }
      }
    }

    return Array.from(reactionsByEmoji.entries()).map(([emoji, reactors]) => ({
      emoji,
      count: reactors.size,
      hasOwnReaction: hasOwnReactionByEmoji.get(emoji) || false,
    }));
  }, [message.reactions, realtimeReactions, currentUserId]);

  // Handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleStatusHistory = () => {
    handleMenuClose();
    setStatusHistoryOpen(true);
  };

  const handleEditHistory = () => {
    handleMenuClose();
    setEditHistoryOpen(true);
  };

  const handleEdit = () => {
    handleMenuClose();
    setEditDialogOpen(true);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleReact = (event: React.MouseEvent<HTMLElement>) => {
    handleMenuClose();
    setReactionAnchorEl(event.currentTarget);
  };

  const handleCopy = async () => {
    handleMenuClose();
    if (message.body) {
      try {
        await navigator.clipboard.writeText(message.body);
        notify(translate('resources.message.notifications.copied'), {
          type: 'info',
        });
      } catch {
        notify(translate('resources.message.notifications.copyFailed'), {
          type: 'error',
        });
      }
    }
  };

  const handleReply = () => {
    handleMenuClose();
    onReply?.(message);
  };

  const handleForward = () => {
    handleMenuClose();
    onForward?.(message);
  };

  const handleSaveEdit = async (newBody: string): Promise<boolean> => {
    const success = await editMessage(message.id, newBody);
    if (success) {
      onMessageUpdated?.();
    }
    return success;
  };

  const handleConfirmDelete = async (hardDelete: boolean): Promise<boolean> => {
    const success = await deleteMessage(message.id, hardDelete);
    if (success) {
      onMessageUpdated?.();
    }
    return success;
  };

  const handleSelectReaction = async (emoji: string) => {
    if (emoji === '') {
      // Remove reaction
      const success = await removeReaction(message.id);
      if (success) {
        onMessageUpdated?.();
      }
    } else {
      // Add reaction
      const success = await reactToMessage(message.id, emoji);
      if (success) {
        onMessageUpdated?.();
      }
    }
  };

  // Click on existing reaction to toggle
  const handleReactionChipClick = async (emoji: string) => {
    if (!canReact) return;

    if (currentUserReaction === emoji) {
      // Remove own reaction
      await removeReaction(message.id);
    } else {
      // Add/change reaction
      await reactToMessage(message.id, emoji);
    }
    onMessageUpdated?.();
  };

  const getSenderName = (msg: Message) =>
    msg.senderUser?.name ||
    msg.senderUser?.email ||
    msg.senderContactIdentity?.name ||
    translate('resources.chat.unknownSender');

  // System messages
  if (isSystem) {
    const systemEventKey = message.metadata?.systemEventKey;
    const systemLabel = systemEventKey
      ? translate(`resources.chat.systemEvents.${systemEventKey}`, {
          _: message.body ?? systemEventKey,
        })
      : message.body;

    return (
      <Box display="flex" justifyContent="center" my={1}>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            bgcolor: 'action.selected',
            px: 2,
            py: 0.5,
            borderRadius: 2,
            fontStyle: 'italic',
          }}
        >
          {systemLabel}
        </Typography>
      </Box>
    );
  }

  // Deleted messages
  if (isDeleted) {
    return (
      <Box
        display="flex"
        justifyContent={isOwnMessage ? 'flex-end' : 'flex-start'}
        my={0.5}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            maxWidth: '70%',
            bgcolor: 'action.hover',
            borderRadius: 2,
            fontStyle: 'italic',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {translate('resources.chat.messageDeleted')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      id={`message-${message.id}`}
      display="flex"
      justifyContent={isOwnMessage ? 'flex-end' : 'flex-start'}
      my={1}
      px={1}
    >
      <Paper
        elevation={1}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => !menuOpen && setIsHovered(false)}
        sx={{
          p: 1.5,
          maxWidth: '70%',
          minWidth: 80,
          bgcolor: isOwnMessage ? colors.outgoing : colors.incoming,
          color: 'text.primary',
          borderRadius: '7.5px',
          borderTopRightRadius: isOwnMessage ? 0 : '7.5px',
          borderTopLeftRadius: isOwnMessage ? '7.5px' : 0,
          position: 'relative',
          boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
          '&:hover': {
            bgcolor: isOwnMessage ? colors.outgoingHover : colors.incomingHover,
          },
        }}
      >
        {/* WhatsApp-style dropdown arrow - appears on hover */}
        {!isOptimisticMessage && (isHovered || menuOpen) && (
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              p: 0.25,
              bgcolor: isOwnMessage ? colors.outgoing : colors.incoming,
              '&:hover': {
                bgcolor: isOwnMessage ? colors.outgoingHover : colors.incomingHover,
              },
              boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
              zIndex: 1,
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: colors.timestamp }} />
          </IconButton>
        )}

        {/* Sender info for non-own messages */}
        {!isOwnMessage && (
          <Typography
            variant="caption"
            fontWeight="600"
            sx={{
              mb: 0.5,
              display: 'block',
              color: colors.senderName,
            }}
          >
            {getSenderName(message)}
          </Typography>
        )}

        {/* Forwarded indicator - WhatsApp style */}
        {isForwarded && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            mb={0.5}
          >
            <ShortcutIcon
              sx={{
                fontSize: 14,
                color: colors.timestamp,
                opacity: 0.8,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: colors.timestamp,
                fontStyle: 'italic',
                fontSize: '0.75rem',
                opacity: 0.8,
              }}
            >
              {translate('resources.chat.forwarded')}
            </Typography>
          </Box>
        )}

        {/* Ephemeral/disappearing message indicator */}
        {isEphemeral && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            mb={0.5}
          >
            <TimerIcon
              sx={{
                fontSize: 14,
                color: colors.timestamp,
                opacity: 0.8,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: colors.timestamp,
                fontStyle: 'italic',
                fontSize: '0.75rem',
                opacity: 0.8,
              }}
            >
              {translate('resources.chat.ephemeral')}
            </Typography>
          </Box>
        )}

        {/* Forum topic indicator */}
        {message.topicId && (
          <Chip
            icon={<ForumIcon sx={{ fontSize: 14 }} />}
            label={
              message.metadata?.telegram?.topicName
                ? String(message.metadata.telegram.topicName)
                : `Topic #${message.topicId}`
            }
            size="small"
            variant="outlined"
            sx={{
              mb: 0.5,
              height: 20,
              fontSize: '0.7rem',
              '& .MuiChip-icon': { ml: 0.5 },
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}

        {/* Private message indicator */}
        {message.isPrivate && (
          <Tooltip title={translate('resources.chat.privateNote')}>
            <Chip
              icon={<LockIcon />}
              label={translate('resources.chat.privateLabel')}
              size="small"
              color="warning"
              sx={{ mb: 1 }}
            />
          </Tooltip>
        )}

        {/* Quoted message - WhatsApp style (click to scroll) */}
        {message.quotedMessage && (
          <Box
            onClick={() => {
              if (!message.quotedMessage?.id) return;
              const el = document.getElementById(`message-${message.quotedMessage.id}`);
              if (!el) return;
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Brief highlight flash on the target message
              el.style.transition = 'background-color 0.3s';
              el.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
              setTimeout(() => { el.style.backgroundColor = ''; }, 1500);
            }}
            sx={{
              mb: 0.75,
              p: 1,
              pl: 1.5,
              bgcolor: isOwnMessage
                ? (isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)')
                : (isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.06)'),
              borderRadius: '7.5px',
              borderLeft: '4px solid',
              borderLeftColor: message.quotedMessage.fromMe || message.quotedMessage.senderUserId
                ? colors.quoteSelf
                : colors.quoteOther,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: isOwnMessage
                  ? (isDarkMode ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.08)')
                  : (isDarkMode ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.09)'),
              },
            }}
          >
            <Typography
              variant="caption"
              fontWeight="600"
              sx={{
                display: 'block',
                color: message.quotedMessage.fromMe || message.quotedMessage.senderUserId
                  ? colors.quoteSelf
                  : colors.quoteOther,
                fontSize: '0.8rem',
                lineHeight: 1.3,
              }}
            >
              {getSenderName(message.quotedMessage)}
            </Typography>
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)',
                fontSize: '0.82rem',
                mt: 0.25,
                lineHeight: 1.3,
              }}
            >
              {message.quotedMessage.body || `[${translate('resources.chat.attachment')}]`}
            </Typography>
          </Box>
        )}

        {/* Message attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {message.attachments.map((attachment, index) => (
              <Box
                key={attachment.id || index}
                sx={{
                  display: 'block',
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <MessageAttachment attachment={attachment} />
              </Box>
            ))}
          </Box>
        )}

        {/* Message body */}
        {message.body && (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {message.body}
          </Typography>
        )}

        {/* Message footer: time, edited, status */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={0.5}
          mt={0.5}
        >
          {message.isEdited && (
            <Tooltip title={translate('resources.chat.edited')}>
              <EditIcon
                sx={{
                  fontSize: 12,
                  color: colors.timestamp,
                }}
              />
            </Tooltip>
          )}

          <Typography
            variant="caption"
            sx={{
              color: colors.timestamp,
              fontSize: 11,
            }}
          >
            {new Date(message.createdAt).toLocaleTimeString(appLocale, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>

          {isOwnMessage && (
            <Box
              component="span"
              sx={{
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                ml: 0.5,
              }}
            >
              <MessageStatusIcon status={message.status} isDarkMode={isDarkMode} />
            </Box>
          )}

          {/* Error indicator + Retry button for failed outgoing messages */}
          {isOwnMessage && message.status === 'FAILED' && message.id > 0 && (
            <>
              <Tooltip
                title={
                  (message.errorDetails as Record<string, unknown>)?.error
                    ? String((message.errorDetails as Record<string, unknown>).error)
                    : translate('resources.message.actions.sendFailed')
                }
              >
                <WarningAmberIcon sx={{ fontSize: 14, ml: 0.5, color: '#f44336' }} />
              </Tooltip>
              {onRetryMessage && (
                <Tooltip title={translate('resources.message.actions.retry')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetryMessage(message.id);
                    }}
                    sx={{
                      ml: 0.25,
                      p: 0.25,
                      color: '#f44336',
                      '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' },
                    }}
                  >
                    <ReplayIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>

        {/* Reactions display - WhatsApp style */}
        {aggregatedReactions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              mt: 0.5,
              ml: -0.5,
            }}
          >
            {aggregatedReactions.map((reaction) => (
              <Chip
                key={reaction.emoji}
                label={`${reaction.emoji}${reaction.count > 1 ? ` ${reaction.count}` : ''}`}
                size="small"
                variant={reaction.hasOwnReaction ? 'filled' : 'outlined'}
                onClick={canReact ? () => handleReactionChipClick(reaction.emoji) : undefined}
                sx={{
                  height: 22,
                  fontSize: 12,
                  cursor: canReact ? 'pointer' : 'default',
                  bgcolor: reaction.hasOwnReaction
                    ? (isDarkMode ? 'rgba(83, 189, 235, 0.2)' : 'rgba(83, 189, 235, 0.15)')
                    : 'transparent',
                  borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)',
                  '& .MuiChip-label': {
                    px: 0.75,
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Message Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={() => {
          handleMenuClose();
          setIsHovered(false);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isOwnMessage ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isOwnMessage ? 'right' : 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 180,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            },
          },
        }}
      >
        {/* Reply */}
        {onReply && (
          <MenuItem onClick={handleReply}>
            <ListItemIcon>
              <ReplyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('resources.message.actions.reply')}
            </ListItemText>
          </MenuItem>
        )}

        {/* Forward - only for channels that support it */}
        {canForward && onForward && (
          <MenuItem onClick={handleForward}>
            <ListItemIcon>
              <ShortcutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('resources.message.actions.forward')}
            </ListItemText>
          </MenuItem>
        )}

        {/* React - only for channels that support it */}
        {canReact && (
          <MenuItem onClick={handleReact}>
            <ListItemIcon>
              <AddReactionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('resources.message.actions.react')}
            </ListItemText>
          </MenuItem>
        )}

        {/* Copy */}
        {message.body && (
          <MenuItem onClick={handleCopy}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('resources.message.actions.copy')}
            </ListItemText>
          </MenuItem>
        )}

        {/* Status History - only for own messages */}
        {isOwnMessage && (
          <MenuItem onClick={handleStatusHistory}>
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('resources.message.actions.statusHistory')}
            </ListItemText>
          </MenuItem>
        )}

        {/* Edit History - only for edited messages */}
        {message.isEdited && (
          <MenuItem onClick={handleEditHistory}>
            <ListItemIcon>
              <EditNoteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('resources.message.actions.editHistory')}
            </ListItemText>
          </MenuItem>
        )}

        {/* Edit - only for own text messages within time limit */}
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={translate('resources.message.actions.edit')}
              secondary={editRemainingTime}
              secondaryTypographyProps={{ variant: 'caption', color: 'warning.main' }}
            />
          </MenuItem>
        )}

        {/* Delete - only for own messages within time limit */}
        {canDelete && <Divider />}
        {canDelete && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText
              primary={translate('resources.message.actions.delete')}
              secondary={deleteRemainingTime}
              secondaryTypographyProps={{ variant: 'caption', color: 'warning.main' }}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Reaction Picker */}
      <EmojiReactionPicker
        anchorEl={reactionAnchorEl}
        open={reactionPickerOpen}
        onClose={() => setReactionAnchorEl(null)}
        onSelect={handleSelectReaction}
        isLoading={isLoading && loadingAction === 'react'}
        currentReaction={currentUserReaction}
      />

      {/* Status History Dialog */}
      {isOwnMessage && !isOptimisticMessage && (
        <MessageStatusHistoryDialog
          open={statusHistoryOpen}
          onClose={() => setStatusHistoryOpen(false)}
          messageId={message.id}
        />
      )}

      {/* Edit History Dialog */}
      {message.isEdited && !isOptimisticMessage && (
        <MessageEditHistoryDialog
          open={editHistoryOpen}
          onClose={() => setEditHistoryOpen(false)}
          messageId={message.id}
          currentBody={message.body}
        />
      )}

      {/* Edit Message Dialog */}
      <MessageEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveEdit}
        currentBody={message.body || ''}
        remainingTime={editRemainingTime}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteMessageConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        remainingTime={deleteRemainingTime}
      />
    </Box>
  );
}
